import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { User, Star, Bookmark, Plus, ArrowRight, LogOut, Building2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServiceCard from "../components/ServiceCard";
import { StarRating } from "../components/StarRating";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [myReviews, setMyReviews] = useState([]);
  const [savedServices, setSavedServices] = useState([]);
  const [myService, setMyService] = useState(null);
  const [mySubs, setMySubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [reviewsRes, savedRes, serviceRes, subsRes] = await Promise.all([
          axios.get(`${API}/my-reviews`, { withCredentials: true }),
          axios.get(`${API}/saved-services`, { withCredentials: true }),
          axios.get(`${API}/my-service`, { withCredentials: true }),
          axios.get(`${API}/my-subs`, { withCredentials: true }),
        ]);

        setMyReviews(reviewsRes.data);
        setSavedServices(savedRes.data);
        setMyService(serviceRes.data);
        setMySubs(subsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleRemoveSaved = async (serviceId) => {
    try {
      await axios.delete(`${API}/saved-services/${serviceId}`, { withCredentials: true });
      setSavedServices(savedServices.filter((s) => s.service_id !== serviceId));
      toast.success("Removed from saved");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="customer-dashboard">
      <Header user={user} />

      <main className="pt-24 lg:pt-28 pb-16 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Profile Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 lg:p-8 mb-8">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              {/* Avatar */}
              <div className="w-20 h-20 rounded-full overflow-hidden bg-[#1A4D2E] flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-white" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-[#1A1A1A] mb-1">{user?.name}</h1>
                <p className="text-[#71717A] mb-2">{user?.email}</p>
                <span className="inline-block px-3 py-1 bg-[#1A4D2E]/10 text-[#1A4D2E] text-sm rounded-full capitalize">
                  {user?.role}
                </span>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                {user?.role === "provider" && myService ? (
                  <Button asChild className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full">
                    <Link to="/provider" data-testid="go-to-provider-btn">
                      <Building2 className="w-4 h-4 mr-2" />
                      Manage Business
                    </Link>
                  </Button>
                ) : (
                  <Button asChild className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full">
                    <Link to="/create-listing" data-testid="list-business-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      List Your Business
                    </Link>
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="border-stone-300 text-[#4A4A4A] rounded-full"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </div>
          </div>

          {/* My Subs Quick Access Card */}
          <div className="bg-gradient-to-br from-[#1A4D2E] to-[#143D24] rounded-2xl shadow-sm p-6 lg:p-8 mb-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">My Subs</h2>
                  <p className="text-white/80">
                    {mySubs.length === 0 
                      ? "Build your subcontractor network"
                      : `${mySubs.length} subcontractor${mySubs.length !== 1 ? 's' : ''} in your network`
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  asChild
                  className="bg-white text-[#1A4D2E] hover:bg-white/90 rounded-full"
                >
                  <Link to="/my-subs" data-testid="view-my-subs-btn">
                    {mySubs.length === 0 ? "Get Started" : "View All"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full"
                >
                  <Link to="/directory">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Subs
                  </Link>
                </Button>
              </div>
            </div>
            
            {/* Preview of subs */}
            {mySubs.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/20">
                <div className="flex flex-wrap gap-3">
                  {mySubs.slice(0, 5).map((sub) => (
                    <Link
                      key={sub.sub_id}
                      to={`/service/${sub.service_id}`}
                      className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 transition-colors"
                    >
                      <span className="text-sm font-medium">{sub.service?.name}</span>
                      {sub.priority_tier === "Primary" && (
                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </Link>
                  ))}
                  {mySubs.length > 5 && (
                    <span className="text-sm text-white/60 flex items-center px-3">
                      +{mySubs.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Dashboard Tabs */}
          <Tabs defaultValue="saved" className="w-full">
            <TabsList className="mb-6 bg-white border border-stone-200 p-1 rounded-xl">
              <TabsTrigger
                value="saved"
                className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white"
                data-testid="saved-tab"
              >
                <Bookmark className="w-4 h-4 mr-2" />
                Saved ({savedServices.length})
              </TabsTrigger>
              <TabsTrigger
                value="reviews"
                className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white"
                data-testid="reviews-tab"
              >
                <Star className="w-4 h-4 mr-2" />
                My Reviews ({myReviews.length})
              </TabsTrigger>
            </TabsList>

            {/* Saved Services */}
            <TabsContent value="saved">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-80 bg-white rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : savedServices.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
                  <Bookmark className="w-12 h-12 text-stone-200 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">No saved services</h3>
                  <p className="text-[#71717A] mb-6">
                    Save services you're interested in to find them easily later
                  </p>
                  <Button asChild className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full">
                    <Link to="/directory">
                      Browse Services
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {savedServices.map((service) => (
                    <div key={service.service_id} className="relative">
                      <ServiceCard service={service} />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveSaved(service.service_id)}
                        className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-500 hover:text-red-600"
                        data-testid={`remove-saved-${service.service_id}`}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* My Reviews */}
            <TabsContent value="reviews">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-32 bg-white rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : myReviews.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
                  <Star className="w-12 h-12 text-stone-200 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">No reviews yet</h3>
                  <p className="text-[#71717A] mb-6">
                    Share your experiences with local service providers
                  </p>
                  <Button asChild className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full">
                    <Link to="/directory">
                      Find Services
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myReviews.map((review) => (
                    <div
                      key={review.review_id}
                      className="bg-white rounded-xl border border-stone-200 p-6"
                      data-testid={`my-review-${review.review_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <Link
                            to={`/service/${review.service_id}`}
                            className="font-semibold text-[#1A1A1A] hover:text-[#1A4D2E]"
                          >
                            {review.service_name || "Service"}
                          </Link>
                          <div className="mt-1">
                            <StarRating rating={review.rating} size="sm" />
                          </div>
                        </div>
                        <span className="text-sm text-[#71717A]">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-[#4A4A4A]">{review.comment}</p>
                      
                      {review.provider_response && (
                        <div className="mt-4 ml-4 pl-4 border-l-2 border-[#1A4D2E]/20">
                          <p className="text-sm font-medium text-[#1A4D2E] mb-1">Provider Response:</p>
                          <p className="text-[#4A4A4A] text-sm">{review.provider_response}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CustomerDashboard;
